import { Component, signal, WritableSignal, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { ApiService, ConfigService, PendingContractsWarningService } from '@app-core/services';
import { OverlayRef } from '@app-core/services/overlay';
import { AccessScope } from '@app-model/access-scopes';
import { DealerInfoResponse } from '@app-model/api';
import { AllDealers, UserResponse } from '@app-model/login';
import { IGetAssociateDealersResponse } from '@app-model/response';
import { AutocompleteContentDirective } from '@app-shared/components/autocomplete/autocomplete-content.directive';
import { AutocompleteComponent } from '@app-shared/components/autocomplete/autocomplete.component';
import { AutocompleteDirective } from '@app-shared/components/autocomplete/autocomplete.directive';
import { OptionComponent } from '@app-shared/components/autocomplete/option/option.component';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { InputComponent } from '@app-shared/components/input/input.component';
import { untilDestroyed } from '@app-shared/utils';
import * as fromRoot from '@app-store/reducers';
import * as userActions from '@app-store/user/user.actions';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

@Component({
  templateUrl: './emulate-dealer-popup.component.html',
  styleUrls: ['./emulate-dealer-popup.component.scss'],
  imports: [
    AutocompleteComponent,
    AutocompleteContentDirective,
    AutocompleteDirective,
    ButtonComponent,
    IconComponent,
    InputComponent,
    OptionComponent,
    ReactiveFormsModule,
  ],
})
export class EmulateDealerPopupComponent {
  private _ref = inject<OverlayRef<EmulateDealerPopupComponent>>(OverlayRef);
  private apiService = inject(ApiService);
  private router = inject(Router);
  private store$ = inject<Store<fromRoot.State>>(Store);
  private warningService = inject(PendingContractsWarningService);
  private _configService = inject(ConfigService);

  user: UserResponse = new UserResponse();
  isValidDealer: boolean = false;

  dealerControl: FormControl<string> = new FormControl<string>('');
  dealers: WritableSignal<DealerInfoResponse[]> = signal([]);

  private _selectedDealer: WritableSignal<DealerInfoResponse | null> = signal(null);

  private selectedAssociatedDealers: AllDealers[] = [];
  private complianceId: string = '';
  private stateId: string = '';
  private compliance: boolean = false;
  private relatedApplicationId: string = '';

  readonly #untilDestroyed = untilDestroyed();
  readonly originalUser = JSON.parse(localStorage.getItem('userState'));

  constructor() {
    this.store$
      .select((state) => state.userState)
      .pipe()
      .subscribe((userState) => {
        if (userState.user) {
          this.user = userState.user;

          const dealerInfo = new DealerInfoResponse(
            this.user.cmsDealerNumber,
            this.user.dealerInfo.dealerCity,
            this.user.dealerInfo.dealerAddress1,
            this.user.dealerInfo.dealerFullName,
            this.user.dealerInfo.dealerName,
            this.user.dealerInfo.dealerPhone,
            this.user.dealerInfo.dealerState,
            this.user.dealerInfo.dealerZipCode,
            true
          );

          this._selectedDealer.set(JSON.parse(JSON.stringify(dealerInfo)));
          this.dealerControl.setValue(dealerInfo.cmsDealerNumber, { emitEvent: false });
          this.stateId = userState.user.dealerInfo.stateId;
          this.complianceId = userState.user.complianceId;
          this.compliance = userState.user.compliance;

          if (!this.user) {
            this.user = new UserResponse();
          }
        }
      });

    this._listenForDealersChange();
  }

  resetDealer() {
    this._getDealerInfo(this.originalUser.originalUser.allDealers[0].cmsDealerNumber, true)
      .pipe(this.#untilDestroyed())
      .subscribe((dealers: DealerInfoResponse[]) => {
        if (dealers?.length) {
          localStorage.setItem('token', dealers[0].token);
        }
        this.store$.dispatch(new userActions.ResetDealerAction());
        void this.router.navigate(['/home']);
        this.warningService.showNotifications();
        this.warningService.showModal();
        this.close();
      });
  }

  setSelectedDealerOption(option: DealerInfoResponse): void {
    this.isValidDealer = !!option.cmsDealerNumber;
    this._selectedDealer.set(option);
    this.dealerControl.patchValue(option.cmsDealerNumber, { emitEvent: false });
  }

  close(): void {
    this._ref.close();
  }

  emulateDealer(): void {
    if (this._selectedDealer() && this.user) {
      this._prepareUser(this._selectedDealer())
        .pipe(
          tap(() => {
            localStorage.setItem('token', this._selectedDealer().token);
            const updateUser = JSON.parse(JSON.stringify(this.user)) as UserResponse;

            const {
              cmsDealerNumber,
              compliance,
              dealerAddress1,
              dealerCity,
              dealerFullName,
              dealerId,
              dealerName,
              dealerPhone,
              dealerState,
              stateId,
            } = this._selectedDealer();

            updateUser.cmsDealerNumber = cmsDealerNumber;
            updateUser.dealerInfo.dealerId = dealerId;
            updateUser.dealerInfo.dealerName = dealerName;
            updateUser.dealerInfo.dealerCity = dealerCity;
            updateUser.dealerInfo.dealerFullName = dealerFullName;
            updateUser.dealerInfo.dealerPhone = dealerPhone;
            updateUser.dealerInfo.dealerAddress1 = dealerAddress1;

            updateUser.dealerInfo.dealerState = dealerState;
            updateUser.dealerInfo.stateId = stateId;
            updateUser.compliance = compliance;
            updateUser.name = dealerName;
            updateUser.allDealers = this.selectedAssociatedDealers;

            const originalUser = JSON.parse(localStorage.getItem('userState'));
            if (originalUser.originalUser?.allDealers[0]?.dealerBrandRestrictions) {
              updateUser.allDealers.forEach((dealer: AllDealers) => {
                dealer['dealerBrandRestrictions'] = originalUser.originalUser.allDealers[0].dealerBrandRestrictions;
              });
            }

            // updating user property
            updateUser.complianceId = this.complianceId;
            updateUser.compliance = this.compliance;
            updateUser.dealerInfo.stateId = this.stateId;
            updateUser.relatedApplicationId = this.relatedApplicationId;

            this.store$.dispatch(new userActions.EmulateDealerAction(updateUser));
            void this.router.navigate(['/home']);

            this.warningService.showNotifications();
            this.warningService.showModal();

            this.close();
          }),
          this.#untilDestroyed()
        )
        .subscribe();
    }
  }

  private _getDealerInfo(dealer: string, getFullInfo: boolean = false): Observable<DealerInfoResponse[]> {
    return this.apiService.getDealerInfo({
      actualDealerGroup: this.user.actualDealerGroup,
      useDealerGroup: this.user.useDealerGroup,
      application: this._configService.appIdentifier,
      programId: this.user.programId,
      dealerId: this.user.dealerInfo.dealerId,
      userName: this.user.userName,
      dealer,
      dealerEmulateAll: Boolean(this.user.scopes.find((scope: AccessScope) => scope === 'DEALER_EMULATE_ALL')),
      getFullInfo,
    });
  }

  private _prepareUser(dealer: DealerInfoResponse): Observable<IGetAssociateDealersResponse> {
    return this._getDealerInfo(dealer.cmsDealerNumber, true).pipe(
      tap((dealers: DealerInfoResponse[]) =>
        this._selectedDealer.set(dealers.length ? { ...dealer, ...dealers[0] } : dealer)
      ),
      switchMap(() =>
        this.apiService.getassociateDealers({
          application: this._configService.appIdentifier,
          programId: this.user.programId,
          cmsDealerNumber: this._selectedDealer().cmsDealerNumber,
        })
      ),
      tap((data: IGetAssociateDealersResponse) => {
        if (data?.allDealers) {
          this.selectedAssociatedDealers = data.allDealers;

          for (const dealer of data.allDealers) {
            if (dealer['primary'] === false) {
              this.relatedApplicationId = dealer.applicationId;
            }
          }

          this.complianceId = data.complianceId;
          this.stateId = data.stateId;
          this.compliance = data.compliance;
        }
      })
    );
  }

  private _searchForDealers(dealer: string): Observable<DealerInfoResponse[]> {
    return this.apiService
      .getDealerInfo({
        actualDealerGroup: this.user.actualDealerGroup,
        useDealerGroup: this.user.useDealerGroup,
        application: this._configService.appIdentifier,
        programId: this.user.programId,
        dealerId: this.user.dealerInfo.dealerId,
        userName: this.user.userName,
        dealer,
        dealerEmulateAll: Boolean(this.user.scopes.find((scope: AccessScope) => scope === 'DEALER_EMULATE_ALL')),
        getFullInfo: false,
      })
      .pipe(
        tap((data: DealerInfoResponse[]) => this.dealers.set(data.length ? data.slice(0, 5) : [])),
        catchError((error: Error) => {
          this.dealers.set([]);
          console.error(error);

          return of([]);
        })
      );
  }

  private _listenForDealersChange(): void {
    this.dealerControl.valueChanges
      .pipe(
        debounceTime(600),
        distinctUntilChanged(),
        filter((val: string) => val?.length > 2),
        switchMap((searchText: string) => this._searchForDealers(searchText)),
        this.#untilDestroyed()
      )
      .subscribe();
  }
}
